import React from "react";

type BadgeVariant = "light" | "solid" | "default" | "destructive" | "outline";
type BadgeSize = "xs" | "sm" | "md" | "lg";
export type BadgeColor =
  | "primary"
  | "success"
  | "error"
  | "warning"
  | "info"
  | "light"
  | "dark"
  | "gray"
  | "blue"
  | "red"
  | "green"
  | "yellow"
  | "purple"
  | "pink"
  | "orange"
  | "indigo";

interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  color?: BadgeColor;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

const Badge: React.FC<BadgeProps> = ({
  variant = "light",
  color = "primary",
  size = "md",
  startIcon,
  endIcon,
  children,
  className = "",
}) => {
  const baseStyles = "inline-flex items-center justify-center gap-1 rounded-full font-medium";

  // Define size styles
  const sizeStyles = {
    xs: "text-xs px-1.5 py-0.5",
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-2.5 py-0.5",
    lg: "text-base px-3 py-1", 
  };

  // Color and variant mapping
  const variantStyles = {
    light: {
      primary: "bg-brand-50 text-brand-500 dark:bg-brand-500/15 dark:text-brand-400",
      success: "bg-green-50 text-green-600 dark:bg-green-500/15 dark:text-green-500",
      error: "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-500",
      warning: "bg-yellow-50 text-yellow-600 dark:bg-yellow-500/15 dark:text-orange-400",
      info: "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-500",
      light: "bg-gray-100 text-gray-700 dark:bg-white/5 dark:text-white/80",
      dark: "bg-gray-800 text-white dark:bg-white/5 dark:text-white",
      gray: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
      blue: "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400",
      red: "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400",
      green: "bg-green-50 text-green-600 dark:bg-green-500/15 dark:text-green-400",
      yellow: "bg-yellow-50 text-yellow-600 dark:bg-yellow-500/15 dark:text-yellow-400",
      purple: "bg-purple-50 text-purple-600 dark:bg-purple-500/15 dark:text-purple-400",
      pink: "bg-pink-50 text-pink-600 dark:bg-pink-500/15 dark:text-pink-400",
      orange: "bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-400",
      indigo: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400",
    },
    solid: {
      primary: "bg-brand-500 text-white dark:bg-brand-600 dark:text-white",
      success: "bg-green-500 text-white dark:bg-green-600 dark:text-white",
      error: "bg-red-500 text-white dark:bg-red-600 dark:text-white",
      warning: "bg-yellow-500 text-white dark:bg-yellow-600 dark:text-white",
      info: "bg-blue-500 text-white dark:bg-blue-600 dark:text-white",
      light: "bg-gray-400 dark:bg-white/5 text-white dark:text-white/80",
      dark: "bg-gray-700 text-white dark:text-white",
      gray: "bg-gray-500 text-white dark:bg-gray-600 dark:text-white",
      blue: "bg-blue-500 text-white dark:bg-blue-600 dark:text-white",
      red: "bg-red-500 text-white dark:bg-red-600 dark:text-white",
      green: "bg-green-500 text-white dark:bg-green-600 dark:text-white",
      yellow: "bg-yellow-500 text-white dark:bg-yellow-600 dark:text-white",
      purple: "bg-purple-500 text-white dark:bg-purple-600 dark:text-white",
      pink: "bg-pink-500 text-white dark:bg-pink-600 dark:text-white",
      orange: "bg-orange-500 text-white dark:bg-orange-600 dark:text-white",
      indigo: "bg-indigo-500 text-white dark:bg-indigo-600 dark:text-white",
    },
    default: {
      primary: "bg-brand-500 text-white hover:bg-brand-600 dark:bg-brand-600 dark:text-white",
      success: "bg-green-500 text-white hover:bg-green-600 dark:bg-green-600 dark:text-white",
      error: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      warning: "bg-yellow-500 text-white hover:bg-yellow-600 dark:bg-yellow-600 dark:text-white",
      info: "bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:text-white",
      light: "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300",
      dark: "bg-gray-800 text-white hover:bg-gray-900 dark:bg-gray-900 dark:text-white",
      gray: "bg-gray-500 text-white hover:bg-gray-600 dark:bg-gray-600 dark:text-white",
      blue: "bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:text-white",
      red: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      green: "bg-green-500 text-white hover:bg-green-600 dark:bg-green-600 dark:text-white",
      yellow: "bg-yellow-500 text-white hover:bg-yellow-600 dark:bg-yellow-600 dark:text-white",
      purple: "bg-purple-500 text-white hover:bg-purple-600 dark:bg-purple-600 dark:text-white",
      pink: "bg-pink-500 text-white hover:bg-pink-600 dark:bg-pink-600 dark:text-white",
      orange: "bg-orange-500 text-white hover:bg-orange-600 dark:bg-orange-600 dark:text-white",
      indigo: "bg-indigo-500 text-white hover:bg-indigo-600 dark:bg-indigo-600 dark:text-white",
    },
    destructive: {
      primary: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      success: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      error: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      warning: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      info: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      light: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      dark: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      gray: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      blue: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      red: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      green: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      yellow: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      purple: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      pink: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      orange: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
      indigo: "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:text-white",
    },
    outline: {
      primary: "border border-brand-500 text-brand-500 hover:bg-brand-50 dark:border-brand-400 dark:text-brand-400",
      success: "border border-green-500 text-green-500 hover:bg-green-50 dark:border-green-400 dark:text-green-400",
      error: "border border-red-500 text-red-500 hover:bg-red-50 dark:border-red-400 dark:text-red-400",
      warning: "border border-yellow-500 text-yellow-500 hover:bg-yellow-50 dark:border-yellow-400 dark:text-yellow-400",
      info: "border border-blue-500 text-blue-500 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400",
      light: "border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300",
      dark: "border border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300",
      gray: "border border-gray-400 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300",
      blue: "border border-blue-400 text-blue-600 hover:bg-blue-50 dark:border-blue-500 dark:text-blue-400",
      red: "border border-red-400 text-red-600 hover:bg-red-50 dark:border-red-500 dark:text-red-400",
      green: "border border-green-400 text-green-600 hover:bg-green-50 dark:border-green-500 dark:text-green-400",
      yellow: "border border-yellow-400 text-yellow-600 hover:bg-yellow-50 dark:border-yellow-500 dark:text-yellow-400",
      purple: "border border-purple-400 text-purple-600 hover:bg-purple-50 dark:border-purple-500 dark:text-purple-400",
      pink: "border border-pink-400 text-pink-600 hover:bg-pink-50 dark:border-pink-500 dark:text-pink-400",
      orange: "border border-orange-400 text-orange-600 hover:bg-orange-50 dark:border-orange-500 dark:text-orange-400",
      indigo: "border border-indigo-400 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-500 dark:text-indigo-400",
    },
  };

  // Get styles based on variant and color
  const variantClass = variantStyles[variant]?.[color] || variantStyles.light.primary;
  const sizeClass = sizeStyles[size];

  return (
    <span className={`${baseStyles} ${sizeClass} ${variantClass} ${className}`}>
      {startIcon && <span className="mr-1">{startIcon}</span>}
      {children}
      {endIcon && <span className="ml-1">{endIcon}</span>}
    </span>
  );
};

export default Badge;